/**
 * Currency configuration with symbols and country codes
 * Used throughout the app for consistent currency display
 */

export interface Currency {
  code: string;
  symbol: string;
  name: string;
  countryCode: string;
}

export const CURRENCIES: Currency[] = [
  { code: 'USD', symbol: '$', name: 'US Dollar', countryCode: 'us' },
  { code: 'EUR', symbol: '€', name: 'Euro', countryCode: 'eu' },
  { code: 'GBP', symbol: '£', name: 'British Pound', countryCode: 'gb' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen', countryCode: 'jp' },
  { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc', countryCode: 'ch' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', countryCode: 'ca' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', countryCode: 'au' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan', countryCode: 'cn' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee', countryCode: 'in' },
  { code: 'KRW', symbol: '₩', name: 'South Korean Won', countryCode: 'kr' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real', countryCode: 'br' },
  { code: 'MXN', symbol: 'MX$', name: 'Mexican Peso', countryCode: 'mx' },
  { code: 'RUB', symbol: '₽', name: 'Russian Ruble', countryCode: 'ru' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand', countryCode: 'za' },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krona', countryCode: 'se' },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone', countryCode: 'no' },
  { code: 'DKK', symbol: 'kr', name: 'Danish Krone', countryCode: 'dk' },
  { code: 'PLN', symbol: 'zł', name: 'Polish Zloty', countryCode: 'pl' },
  { code: 'THB', symbol: '฿', name: 'Thai Baht', countryCode: 'th' },
  { code: 'IDR', symbol: 'Rp', name: 'Indonesian Rupiah', countryCode: 'id' },
  { code: 'HUF', symbol: 'Ft', name: 'Hungarian Forint', countryCode: 'hu' },
  { code: 'CZK', symbol: 'Kč', name: 'Czech Koruna', countryCode: 'cz' },
  { code: 'ILS', symbol: '₪', name: 'Israeli Shekel', countryCode: 'il' },
  { code: 'CLP', symbol: '$', name: 'Chilean Peso', countryCode: 'cl' },
  { code: 'PHP', symbol: '₱', name: 'Philippine Peso', countryCode: 'ph' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham', countryCode: 'ae' },
  { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal', countryCode: 'sa' },
  { code: 'MYR', symbol: 'RM', name: 'Malaysian Ringgit', countryCode: 'my' },
  { code: 'TRY', symbol: '₺', name: 'Turkish Lira', countryCode: 'tr' },
  { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar', countryCode: 'nz' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar', countryCode: 'sg' },
  { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar', countryCode: 'hk' },
  { code: 'ARS', symbol: '$', name: 'Argentine Peso', countryCode: 'ar' },
  { code: 'COP', symbol: '$', name: 'Colombian Peso', countryCode: 'co' },
  { code: 'PEN', symbol: 'S/', name: 'Peruvian Sol', countryCode: 'pe' },
  { code: 'VND', symbol: '₫', name: 'Vietnamese Dong', countryCode: 'vn' },
  { code: 'EGP', symbol: '£', name: 'Egyptian Pound', countryCode: 'eg' },
  { code: 'NGN', symbol: '₦', name: 'Nigerian Naira', countryCode: 'ng' },
  { code: 'PKR', symbol: '₨', name: 'Pakistani Rupee', countryCode: 'pk' },
  { code: 'BDT', symbol: '৳', name: 'Bangladeshi Taka', countryCode: 'bd' },
  { code: 'UAH', symbol: '₴', name: 'Ukrainian Hryvnia', countryCode: 'ua' },
  { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling', countryCode: 'ke' },
];

// Helper functions
export const getCurrencyByCode = (code: string): Currency | undefined => {
  return CURRENCIES.find(c => c.code === code.toUpperCase());
};

export const getCurrencySymbol = (code: string): string => {
  const currency = getCurrencyByCode(code);
  return currency?.symbol || code;
};

export const getCountryCode = (code: string): string => {
  const currency = getCurrencyByCode(code);
  return currency?.countryCode || 'un';
};
